// PICK A CUSTOMER. DO NOT TYPE ONE.
//
// This replaces a free-text box, and the change is the point rather than a
// nicety. Typing "Pinewell" matched whichever Pinewell the database found
// first, so two real companies of that name were one record; a typo created a
// third company that looked like a successful save. Neither said anything.
//
// The flow is deliberately two steps, entity then addresses: a customer's
// ship-to list only exists once you know which customer, and a ship-to is
// what carries the GSTIN that ends up on the document.
//
// Creating a customer is an explicit act here too -- a button, a form, and a
// save -- never a side effect of typing a name nothing matched.

import { useEffect, useRef, useState } from "react";
import { Search, Plus, X } from "lucide-react";
import { searchCustomers, getCustomerAddresses, createCustomer, findCustomersByGstin } from "@/services/api";
import type { CustomerPartner, QuoteCustomer } from "@/types";
// ⚠️ THE DUPLICATE DECISION IS A PURE MODULE, and its name rule is
// sameCompany from poWarnings.ts -- reused, not re-derived, so the PO screen
// and this picker cannot drift into disagreeing about whether two names are
// the same company.
import {
  findDuplicateCustomer, customerSearchTerm, duplicateMessage,
  type DuplicateMatch,
} from "@/services/customerDuplicate";
import {
  colors, semanticColors, radius, spacing, heights, typography,
  iconSize, iconStroke, layout,
} from "@/styles/tokens";

const INPUT: React.CSSProperties = {
  width: "100%", height: heights.input, padding: `0 ${spacing[10]}px`,
  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface, outline: "none", boxSizing: "border-box",
};

const LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium, marginBottom: spacing[4],
  display: "block",
};

// Advisory only, exactly as Settings > Company treats it: the tax office is
// the authority on this format, not this form, and a wrong regex must never
// stop a correct GSTIN being saved.
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export function gstinLooksWrong(value: string): boolean {
  return value.trim() !== "" && !GSTIN_RE.test(value.trim().toUpperCase());
}

/** One line of address, as a document would print it. */
export function addressLine(p: CustomerPartner | null | undefined): string {
  if (!p) return "";
  return [p.street, p.street2, p.city, p.zip, p.state]
    .filter(Boolean)
    .join(", ");
}

/** What the user is typing into the create form.
 *
 *  Flat and stringly-typed on purpose: it is a form, and it becomes the
 *  backend's nested inline-create payload in exactly one place
 *  (draftToCreatePayload) rather than being assembled in the JSX. */
export interface NewCustomerDraft {
  name: string;
  vat: string;
  street: string;
  city: string;
  zip: string;
  shipName: string;
  shipVat: string;
  shipStreet: string;
  shipCity: string;
  contactName: string;
  contactEmail: string;
}

export const EMPTY_DRAFT: NewCustomerDraft = {
  name: "", vat: "", street: "", city: "", zip: "",
  shipName: "", shipVat: "", shipStreet: "", shipCity: "",
  contactName: "", contactEmail: "",
};

/** The draft as the backend's `customer` inline-create payload.
 *
 *  Only what was filled in is sent. An empty ship-to block means no ship-to
 *  is created -- not one created blank, which would be a real address record
 *  with nothing in it. */
export function draftToCreatePayload(draft: NewCustomerDraft): Record<string, unknown> {
  const payload: Record<string, unknown> = { name: draft.name.trim() };
  if (draft.vat.trim()) payload.vat = draft.vat.trim().toUpperCase();
  if (draft.street.trim()) payload.street = draft.street.trim();
  if (draft.city.trim()) payload.city = draft.city.trim();
  if (draft.zip.trim()) payload.zip = draft.zip.trim();

  if (draft.shipName.trim()) {
    const shipping: Record<string, unknown> = { name: draft.shipName.trim() };
    // The backend sets is_company on any child carrying a vat, which is what
    // lets a branch keep its OWN GSTIN instead of inheriting the parent's.
    if (draft.shipVat.trim()) shipping.vat = draft.shipVat.trim().toUpperCase();
    if (draft.shipStreet.trim()) shipping.street = draft.shipStreet.trim();
    if (draft.shipCity.trim()) shipping.city = draft.shipCity.trim();
    payload.shipping = shipping;
  }

  if (draft.contactName.trim()) {
    const contact: Record<string, unknown> = { name: draft.contactName.trim() };
    if (draft.contactEmail.trim()) contact.email = draft.contactEmail.trim();
    payload.contact = contact;
  }

  return payload;
}

function DraftField({
  label, testid, value, onChange, placeholder, warning,
}: {
  label: string; testid: string; value: string;
  onChange: (v: string) => void; placeholder?: string; warning?: string;
}) {
  return (
    <div>
      <span style={LABEL}>{label}</span>
      <input
        data-testid={testid}
        style={INPUT}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {warning && (
        <div
          data-testid={`${testid}-warning`}
          style={{ marginTop: spacing[4], fontSize: typography.fontSize.xs, color: colors.amber600 }}
        >
          {warning}
        </div>
      )}
    </div>
  );
}

function RoleSelect({
  label, testid, options, value, onChange, emptyNote,
}: {
  label: string;
  testid: string;
  options: CustomerPartner[];
  value: CustomerPartner | null | undefined;
  onChange: (p: CustomerPartner | null) => void;
  emptyNote: string;
}) {
  return (
    <div>
      <span style={LABEL}>{label}</span>
      {options.length === 0 ? (
        <div
          data-testid={`${testid}-empty`}
          style={{
            fontSize: typography.fontSize.sm, color: semanticColors.textPlaceholder,
            height: heights.input, display: "flex", alignItems: "center",
          }}
        >
          {emptyNote}
        </div>
      ) : (
        <select
          data-testid={testid}
          style={INPUT}
          value={value?.id ?? ""}
          onChange={(e) => {
            const id = Number(e.target.value);
            onChange(options.find((o) => o.id === id) ?? null);
          }}
        >
          <option value="">— none —</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}{o.city ? ` — ${o.city}` : ""}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

export function CustomerPicker({
  value,
  onChange,
}: {
  value: QuoteCustomer | undefined;
  onChange: (customer: QuoteCustomer | undefined) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CustomerPartner[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [gstinNotes, setGstinNotes] = useState<string[]>([]);
  const [addresses, setAddresses] = useState<{
    shipping: CustomerPartner[];
    invoice: CustomerPartner[];
    contact: CustomerPartner[];
  }>({ shipping: [], invoice: [], contact: [] });

  // The inline-create form. `null` means closed -- a customer is created by
  // asking for it, never as a side effect of a search finding nothing.
  const [draft, setDraft] = useState<NewCustomerDraft | null>(null);
  const [creating, setCreating] = useState(false);
  // ⚠️ AN EXISTING CUSTOMER THIS DRAFT WOULD DUPLICATE. Null means either
  // "not checked yet" or "checked, nothing found" -- and the difference does
  // not matter, because a create is only ever reached through onCreate below,
  // which checks first. Set means the question is on screen and nothing has
  // been written.
  const [duplicate, setDuplicate] = useState<DuplicateMatch | null>(null);
  const [checking, setChecking] = useState(false);

  /** Write the customer. Reached only after the duplicate check, or from
   *  "Create anyway". This is the ORIGINAL create, unchanged. */
  async function writeCustomer(draftNow: NewCustomerDraft) {
    setCreating(true);
    setError("");
    const result = await createCustomer(draftToCreatePayload(draftNow));
    setCreating(false);
    if (!result.ok || !result.entity) {
      setError(result.error ?? "Could not create this customer.");
      return;
    }
    setGstinNotes(result.gstinNotes ?? []);
    // Select what was just created, including the ship-to, so the quote is
    // addressed to it without a second step.
    onChange({
      entity: result.entity,
      shipping: result.shipping ?? null,
      invoice: null,
      contact: result.contact ?? null,
    });
    setDraft(null);
    setDuplicate(null);
    setResults([]);
  }

  /** "Create customer" pressed.
   *
   *  ⚠️ THE CHECK RUNS FIRST AND CREATES NOTHING. If it finds an existing
   *  active customer that is the same company, the question goes on screen and
   *  this returns -- no request is made. Otherwise the create proceeds exactly
   *  as it always did, which is the fence on this slice: WITH NO MATCH, THE
   *  FLOW IS UNCHANGED.
   *
   *  ⚠️ AND A FAILED CHECK DOES NOT BLOCK THE CREATE. searchCustomers returns
   *  ok:false on a dead session or a broken backend; treating that as "no
   *  duplicate" is the right fallback for a WARNING -- refusing to create a
   *  customer because we could not look for one would turn an advisory into an
   *  outage. The save itself still reports its own failure. */
  async function onCreatePressed() {
    if (!draft) return;
    const term = customerSearchTerm(draft.name);
    const gstin = draft.vat.trim();
    if (!term && !gstin) { await writeCustomer(draft); return; }

    setChecking(true);
    setError("");

    // ⚠️ THE GSTIN CHECK IS THE BACKEND'S, EXACT AT ANY SIZE
    // (customer-snapshot-at-finalise, 2026-09-23). It replaced a scan of the
    // first 200 customers, which missed a holder past that page. A GSTIN is a
    // registration number, so a match is reported first and as the reason --
    // whatever the holder is called. A failed check falls through to the
    // name check and then to the create: a WARNING must never become an
    // outage.
    if (gstin) {
      const byGstin = await findCustomersByGstin(gstin);
      const holder = byGstin.ok ? (byGstin.customers ?? [])[0] : undefined;
      if (holder) {
        setChecking(false);
        setDuplicate({ existing: holder, reason: "gstin" });
        return;
      }
    }
    if (!term) { setChecking(false); await writeCustomer(draft); return; }
    // ⚠️ A BROAD CANDIDATE SEARCH AND A NARROW DECISION. The server matches
    // `name ilike <term>` on the first significant word, so "Larkspur" surfaces
    // a stored "Larkspur India Pvt Ltd" for a typed "Larkspur India Private
    // Limited"; findDuplicateCustomer then decides on whole-entity equality.
    // The endpoint's domain excludes archived partners, which is what keeps an
    // archived duplicate from ever being offered.
    const byName = await searchCustomers(term, 50);
    setChecking(false);

    const match = findDuplicateCustomer(draft.name, draft.vat, byName.customers ?? []);
    if (match) { setDuplicate(match); return; }
    await writeCustomer(draft);
  }

  /** "Use existing" — selects the record that already exists and CREATES
   *  NOTHING.
   *
   *  ⚠️ IT GOES THROUGH `chooseEntity`, THE SAME PATH AS PICKING THE CUSTOMER
   *  OUT OF THE SEARCH RESULTS. So the outcome is indistinguishable from
   *  having found it in the first place: the roles are cleared and the
   *  existing effect loads that entity's addresses. Fetching them here
   *  instead would have been a second, divergent way to select a customer --
   *  and it would have raced the effect that does it anyway. */
  function onUseExisting(existing: CustomerPartner) {
    setDuplicate(null);
    setError("");
    setDraft(null);
    chooseEntity(existing);
  }

  const entity = value?.entity ?? null;

  // Load the entity's addresses whenever one is selected -- including on
  // reopen, where the entity arrives already chosen and its lists have to be
  // repopulated for the dropdowns to show what was picked.
  const loadedFor = useRef<number | null>(null);
  useEffect(() => {
    if (!entity?.id) {
      setAddresses({ shipping: [], invoice: [], contact: [] });
      loadedFor.current = null;
      return;
    }
    if (loadedFor.current === entity.id) return;

    let cancelled = false;
    (async () => {
      const result = await getCustomerAddresses(entity.id);
      if (cancelled) return;
      if (result.ok && result.addresses) {
        loadedFor.current = entity.id;
        setAddresses({
          shipping: result.addresses.shipping ?? [],
          invoice: result.addresses.invoice ?? [],
          contact: result.addresses.contact ?? [],
        });
      } else {
        setError(result.error ?? "Could not load this customer's addresses.");
      }
    })();
    return () => { cancelled = true; };
  }, [entity?.id]);

  const runSearch = async () => {
    setSearching(true);
    setError("");
    const result = await searchCustomers(query);
    setSearching(false);
    if (!result.ok) {
      setError(result.error ?? "Customer search failed.");
      setResults([]);
      return;
    }
    setResults(result.customers ?? []);
  };

  const chooseEntity = (partner: CustomerPartner) => {
    // A different entity invalidates the roles under the old one -- they
    // belong to a company this quote is no longer for, and the backend
    // refuses that combination anyway.
    onChange({ entity: partner, shipping: null, invoice: null, contact: null });
    setResults([]);
  };

  const setRole = (role: keyof QuoteCustomer, partner: CustomerPartner | null) => {
    if (!entity) return;
    onChange({ ...value, entity, [role]: partner });
  };

  return (
    <div data-testid="customer-picker" style={{ display: "flex", flexDirection: "column", gap: spacing[12] }}>
      {!entity ? (
        <div>
          <span style={LABEL}>Customer</span>
          <div className="flex items-center" style={{ gap: spacing[8] }}>
            <div className="flex items-center" style={{ flex: 1, position: "relative" }}>
              <Search
                size={iconSize.md}
                strokeWidth={iconStroke.regular}
                color={semanticColors.textPlaceholder}
                style={{ position: "absolute", left: spacing[8] }}
              />
              <input
                data-testid="customer-search"
                style={{ ...INPUT, paddingLeft: spacing[32] }}
                placeholder="Search customers by name"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
              />
            </div>
            <button
              type="button"
              data-testid="customer-search-btn"
              onClick={runSearch}
              disabled={searching}
              style={{
                height: heights.input, padding: `0 ${spacing[14]}px`,
                backgroundColor: colors.indigo600, color: semanticColors.onAccent,
                border: "none", borderRadius: radius.base,
                fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                cursor: searching ? "default" : "pointer", opacity: searching ? 0.6 : 1,
              }}
            >
              {searching ? "Searching…" : "Search"}
            </button>
          </div>

          {results.length > 0 && (
            <div
              data-testid="customer-results"
              style={{
                marginTop: spacing[8], border: `1px solid ${semanticColors.border}`,
                borderRadius: radius.base, maxHeight: 200, overflowY: "auto",
                backgroundColor: semanticColors.surface,
              }}
            >
              {results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`customer-result-${r.id}`}
                  onClick={() => chooseEntity(r)}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: `${spacing[8]}px ${spacing[10]}px`, border: "none",
                    borderBottom: `1px solid ${semanticColors.border}`,
                    backgroundColor: "transparent", cursor: "pointer",
                    fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
                  }}
                >
                  <div style={{ fontWeight: typography.fontWeight.medium }}>{r.name}</div>
                  {/* GSTIN and city are what tell two same-named companies
                      apart, which is the whole reason this is a picker. */}
                  <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
                    {[r.vat, r.city].filter(Boolean).join(" · ") || "no GSTIN on record"}
                  </div>
                </button>
              ))}
            </div>
          )}

          {searching === false && results.length === 0 && query !== "" && !error && !draft && (
            <div
              data-testid="customer-no-results"
              style={{ marginTop: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.textMuted }}
            >
              No customer matched. Typing a name here does not create one —
              use “New customer” below.
            </div>
          )}

          {!draft && (
            <button
              type="button"
              data-testid="customer-new-btn"
              onClick={() => setDraft({ ...EMPTY_DRAFT, name: query.trim() })}
              className="flex items-center"
              style={{
                marginTop: spacing[8], gap: spacing[6], background: "none",
                border: "none", cursor: "pointer", padding: 0,
                color: semanticColors.accentTextOnTint, fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
              }}
            >
              <Plus size={iconSize.md} strokeWidth={iconStroke.regular} />
              New customer
            </button>
          )}

          {draft && (
            <div
              data-testid="customer-create-form"
              style={{
                marginTop: spacing[12], padding: spacing[12],
                border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                backgroundColor: semanticColors.panelBg,
                display: "flex", flexDirection: "column", gap: spacing[12],
              }}
            >
              <div style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary }}>
                New customer
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[12] }}>
                <DraftField
                  label="Company Name" testid="new-customer-name"
                  value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })}
                  placeholder="Registered name"
                />
                <DraftField
                  label="GSTIN" testid="new-customer-gstin"
                  value={draft.vat} onChange={(v) => setDraft({ ...draft, vat: v })}
                  placeholder="15 characters"
                  warning={gstinLooksWrong(draft.vat) ? "This does not look like a GSTIN. It will still be saved." : undefined}
                />
                <DraftField
                  label="Street" testid="new-customer-street"
                  value={draft.street} onChange={(v) => setDraft({ ...draft, street: v })}
                />
                <DraftField
                  label="City" testid="new-customer-city"
                  value={draft.city} onChange={(v) => setDraft({ ...draft, city: v })}
                />
              </div>

              {/* Optional. A branch with its own GSTIN is the case this whole
                  model exists for, so it is offered here rather than forcing a
                  second trip through Odoo. */}
              <div style={{ borderTop: `1px solid ${semanticColors.border}`, paddingTop: spacing[12] }}>
                <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted, textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide, marginBottom: spacing[8] }}>
                  Ship-to (optional)
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[12] }}>
                  <DraftField
                    label="Site Name" testid="new-customer-ship-name"
                    value={draft.shipName} onChange={(v) => setDraft({ ...draft, shipName: v })}
                    placeholder="e.g. Pune Plant"
                  />
                  <DraftField
                    label="Ship-To GSTIN" testid="new-customer-ship-gstin"
                    value={draft.shipVat} onChange={(v) => setDraft({ ...draft, shipVat: v })}
                    warning={gstinLooksWrong(draft.shipVat) ? "This does not look like a GSTIN. It will still be saved." : undefined}
                  />
                  <DraftField
                    label="Street" testid="new-customer-ship-street"
                    value={draft.shipStreet} onChange={(v) => setDraft({ ...draft, shipStreet: v })}
                  />
                  <DraftField
                    label="City" testid="new-customer-ship-city"
                    value={draft.shipCity} onChange={(v) => setDraft({ ...draft, shipCity: v })}
                  />
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${semanticColors.border}`, paddingTop: spacing[12] }}>
                <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted, textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide, marginBottom: spacing[8] }}>
                  Contact (optional)
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[12] }}>
                  <DraftField
                    label="Name" testid="new-customer-contact-name"
                    value={draft.contactName} onChange={(v) => setDraft({ ...draft, contactName: v })}
                  />
                  <DraftField
                    label="Email" testid="new-customer-contact-email"
                    value={draft.contactEmail} onChange={(v) => setDraft({ ...draft, contactEmail: v })}
                  />
                </div>
              </div>

              {/* ⚠️ THE DUPLICATE QUESTION. A WARNING, NOT A REFUSAL:
                  "Create anyway" is right there and the create button is not
                  disabled behind this. Amber, because it is a difference for a
                  person to judge -- the same treatment the PO warnings get.

                  ⚠️ NOTHING HAS BEEN WRITTEN AT THIS POINT. onCreatePressed
                  returns before the create request when a match is found, so
                  "Use existing" leaves the database exactly as it was. */}
              {duplicate && (
                <div
                  data-testid="customer-duplicate-warning"
                  data-duplicate-reason={duplicate.reason}
                  data-existing-id={duplicate.existing.id}
                  role="alertdialog"
                  aria-label="This customer may already exist"
                  style={{
                    padding: spacing[12],
                    backgroundColor: colors.amber50,
                    border: `1px solid ${colors.amber200}`,
                    borderRadius: radius.base,
                  }}
                >
                  <div style={{
                    fontSize: typography.fontSize.base,
                    color: semanticColors.textPrimary,
                    marginBottom: spacing[4],
                  }}>
                    {duplicateMessage(duplicate)}
                  </div>
                  {/* Says WHY it matched, so a person can tell a name
                      coincidence from a shared registration number. */}
                  <div style={{
                    fontSize: typography.fontSize.xs,
                    color: semanticColors.textSecondary,
                    marginBottom: spacing[10],
                  }}>
                    {duplicate.reason === "gstin"
                      ? "It carries the same GSTIN."
                      : "The names match once punctuation and Pvt/Ltd are ignored."}
                  </div>
                  <div className="flex items-center" style={{ gap: spacing[8] }}>
                    <button
                      type="button"
                      data-testid="customer-duplicate-use-existing"
                      disabled={creating}
                      onClick={() => onUseExisting(duplicate.existing)}
                      style={{
                        height: heights.control, padding: `0 ${spacing[14]}px`,
                        backgroundColor: colors.indigo600, color: semanticColors.onAccent,
                        border: "none", borderRadius: radius.base,
                        fontSize: typography.fontSize.base,
                        fontWeight: typography.fontWeight.medium,
                        cursor: "pointer",
                      }}
                    >
                      Use existing
                    </button>
                    <button
                      type="button"
                      data-testid="customer-duplicate-create-anyway"
                      disabled={creating}
                      onClick={() => { if (draft) void writeCustomer(draft); }}
                      style={{
                        height: heights.control, padding: `0 ${spacing[14]}px`,
                        backgroundColor: semanticColors.surface,
                        color: semanticColors.textSecondary,
                        border: `1px solid ${semanticColors.border}`,
                        borderRadius: radius.base,
                        fontSize: typography.fontSize.base, cursor: "pointer",
                      }}
                    >
                      {creating ? "Creating…" : "Create anyway"}
                    </button>
                  </div>
                </div>
              )}

              {/* P3-7: sticky to the bottom of the modal's scrolling body, so
                  Create is never below the fold however long the form is. */}
              <div
                data-testid="customer-create-actions"
                className="flex items-center"
                style={{
                  gap: spacing[8], position: "sticky", bottom: 0, zIndex: 1,
                  backgroundColor: semanticColors.panelBg,
                  paddingTop: spacing[8], paddingBottom: spacing[8],
                  borderTop: `1px solid ${semanticColors.border}`,
                }}
              >
                <button
                  type="button"
                  data-testid="customer-create-submit"
                  disabled={draft.name.trim() === "" || creating || checking}
                  onClick={() => void onCreatePressed()}
                  style={{
                    height: heights.control, padding: `0 ${spacing[14]}px`,
                    backgroundColor: colors.indigo600, color: semanticColors.onAccent,
                    border: "none", borderRadius: radius.base,
                    fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                    cursor: draft.name.trim() === "" || creating || checking ? "not-allowed" : "pointer",
                    opacity: draft.name.trim() === "" || creating || checking ? 0.6 : 1,
                  }}
                >
                  {checking ? "Checking…" : creating ? "Creating…" : "Create customer"}
                </button>
                <button
                  type="button"
                  data-testid="customer-create-cancel"
                  onClick={() => setDraft(null)}
                  style={{
                    height: heights.control, padding: `0 ${spacing[14]}px`,
                    backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                    border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                    fontSize: typography.fontSize.base, cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <span style={LABEL}>Customer</span>
          <div
            data-testid="customer-selected"
            className="flex items-center justify-between"
            style={{
              border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
              padding: `${spacing[8]}px ${spacing[10]}px`, backgroundColor: semanticColors.panelBg,
            }}
          >
            <div>
              <div style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary }}>
                {entity.name}
              </div>
              <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
                {[entity.vat, entity.city].filter(Boolean).join(" · ") || "no GSTIN on record"}
              </div>
            </div>
            <button
              type="button"
              data-testid="customer-clear"
              onClick={() => onChange(undefined)}
              title="Choose a different customer"
              style={{ background: "none", border: "none", cursor: "pointer", color: semanticColors.textMuted, display: "flex" }}
            >
              <X size={iconSize.md} strokeWidth={iconStroke.regular} />
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[12], marginTop: spacing[12] }}>
            <RoleSelect
              label="Ship To"
              testid="customer-shipping"
              options={addresses.shipping}
              value={value?.shipping}
              onChange={(p) => setRole("shipping", p)}
              emptyNote="No delivery address on this customer."
            />
            <RoleSelect
              label="Bill To"
              testid="customer-invoice"
              options={addresses.invoice}
              value={value?.invoice}
              onChange={(p) => setRole("invoice", p)}
              emptyNote="No billing address on this customer."
            />
            <RoleSelect
              label="Contact"
              testid="customer-contact"
              options={addresses.contact}
              value={value?.contact}
              onChange={(p) => setRole("contact", p)}
              emptyNote="No contact on this customer."
            />
            {value?.shipping?.vat && (
              <div>
                <span style={LABEL}>Ship-To GSTIN</span>
                <div
                  data-testid="customer-shipping-gstin"
                  style={{
                    height: heights.input, display: "flex", alignItems: "center",
                    fontSize: typography.fontSize.base, fontVariantNumeric: "tabular-nums",
                    color: semanticColors.textPrimary,
                  }}
                >
                  {value.shipping.vat}
                </div>
              </div>
            )}
          </div>

          {value?.shipping && (
            <div
              data-testid="customer-shipping-address"
              style={{ marginTop: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}
            >
              {addressLine(value.shipping) || "No address recorded for this ship-to."}
            </div>
          )}
        </div>
      )}

      {error && (
        <div data-testid="customer-picker-error" style={{ fontSize: typography.fontSize.sm, color: semanticColors.destructive }}>
          {error}
        </div>
      )}
      {/* state-from-gstin: the customer WAS created; its State disagrees
          with its GSTIN, which someone should check. */}
      {gstinNotes.map((note) => (
        <div key={note} data-testid="customer-picker-gstin-note" style={{
          fontSize: typography.fontSize.sm, color: semanticColors.warningTextOnTint,
          backgroundColor: semanticColors.warningBg, border: `1px solid ${semanticColors.warningBorder}`,
          borderRadius: radius.base, padding: `${spacing[6]}px ${spacing[8]}px`,
        }}>
          {note}
        </div>
      ))}
    </div>
  );
}
